import {
  BILLER_MANAGER_PERMISSION_FLAGS,
  CHART_EXPORT_PERMISSION_FLAGS,
  FLAG_WRITE_PERMISSION_FLAGS,
  FORMS_SEND_PERMISSION_FLAGS,
  LAB_RESULTS_VIEW_PERMISSION_FLAGS,
  PractitionerPermissionFlags,
  PROVIDER_PERMISSION_FLAGS,
  WRITE_PERMISSION_FLAGS,
} from "@/permission/constants/practitioner-permissions";

const hasAdminRole = (permissions: PractitionerPermissionFlags[]) => {
  return permissions.includes(PractitionerPermissionFlags.Admin);
};

const hasAdminProviderRole = (permissions: PractitionerPermissionFlags[]) => {
  return (
    hasAdminRole(permissions) || permissions.includes(PractitionerPermissionFlags.AdminProvider)
  );
};

const hasManagerRole = (permissions: PractitionerPermissionFlags[]) => {
  return (
    hasAdminProviderRole(permissions) ||
    permissions.includes(PractitionerPermissionFlags.ManagerRole)
  );
};

const hasProviderRole = (permissions: PractitionerPermissionFlags[]) => {
  return (
    hasAdminProviderRole(permissions) ||
    permissions.includes(PractitionerPermissionFlags.ProviderRole)
  );
};

const hasScribeRole = (permissions: PractitionerPermissionFlags[]) => {
  return (
    hasAdminProviderRole(permissions) ||
    permissions.includes(PractitionerPermissionFlags.ScribeRole)
  );
};

const hasBillerRole = (permissions: PractitionerPermissionFlags[]) => {
  return (
    hasAdminProviderRole(permissions) ||
    permissions.includes(PractitionerPermissionFlags.BillerRole)
  );
};

const hasReaderRole = (permissions: PractitionerPermissionFlags[]) => {
  return (
    hasAdminProviderRole(permissions) ||
    permissions.includes(PractitionerPermissionFlags.ReaderRole)
  );
};

/**
 * Whether the user holds any role that may write. Read-Only is the one staff role that never
 * writes, so every other role qualifies.
 *
 * Membership is tested against WRITE_PERMISSION_FLAGS rather than by excluding ReaderRole, so an
 * unrecognised role coding grants nothing and this stays equivalent to the PermissionGate form.
 */
const hasWriteRole = (permissions: PractitionerPermissionFlags[]) => {
  return permissions.some((permission) => WRITE_PERMISSION_FLAGS.includes(permission));
};

const hasDoulaRole = (permissions: PractitionerPermissionFlags[]) => {
  return (
    hasAdminProviderRole(permissions) || permissions.includes(PractitionerPermissionFlags.DoulaRole)
  );
};

/** Clinical charting access. Includes Doulas, who chart like Providers. */
const hasProviderOrScribeRole = (permissions: PractitionerPermissionFlags[]) => {
  return hasProviderRole(permissions) || hasScribeRole(permissions) || hasDoulaRole(permissions);
};

/**
 * Whether the user may place a lab order.
 *
 * A Doula cannot order labs, but the flag alone is not disqualifying: someone
 * holding DoulaRole alongside a role that may order (Provider, Scribe, Admin
 * Provider) keeps that capability. Only a Doula with no ordering role is blocked.
 *
 * Scribes may order because their orders land in NeedsApproval for a Provider
 * to transmit.
 *
 * This gate covers the UI only. Doulas share the care-provider-role AccessPolicy
 * with Providers, so the Medplum API still accepts lab-order writes from them.
 */
const hasLabOrderPermission = (permissions: PractitionerPermissionFlags[]) => {
  return hasProviderRole(permissions) || hasScribeRole(permissions);
};

/**
 * Whether the user may sign, lock and amend an encounter, and follow up on it: closing out the
 * pregnancy after an L&D sign-off, or opening the amend screen from a link. For staff roles, this
 * matches the Sign & Lock and Amend buttons, which gate on PROVIDER_PERMISSION_FLAGS. Includes
 * Doulas; excludes Scribes.
 */
const hasEncounterSignPermission = (permissions: PractitionerPermissionFlags[]) => {
  return hasRequiredPermissions(permissions, PROVIDER_PERMISSION_FLAGS);
};

const hasChartExportPermission = (permissions: PractitionerPermissionFlags[]) => {
  return permissions.some((p) => CHART_EXPORT_PERMISSION_FLAGS.includes(p));
};

/** Whether the user may view a client's lab results and Lab Flow Sheet. Viewing only. */
const hasLabResultsViewPermission = (permissions: PractitionerPermissionFlags[]) => {
  return permissions.some((p) => LAB_RESULTS_VIEW_PERMISSION_FLAGS.includes(p));
};

/** Whether the user may apply or remove a flag on a client's chart. See FLAG_WRITE_PERMISSION_FLAGS. */
const hasFlagWritePermission = (permissions: PractitionerPermissionFlags[]) => {
  return permissions.some((p) => FLAG_WRITE_PERMISSION_FLAGS.includes(p));
};

/** Whether the user may send a form to a client. See FORMS_SEND_PERMISSION_FLAGS. */
const hasFormsSendPermission = (permissions: PractitionerPermissionFlags[]) => {
  return permissions.some((permission) => FORMS_SEND_PERMISSION_FLAGS.includes(permission));
};

const hasAnyPermission = (permissions: PractitionerPermissionFlags[]) => {
  return (
    hasReaderRole(permissions) ||
    hasProviderRole(permissions) ||
    hasBillerRole(permissions) ||
    hasScribeRole(permissions) ||
    hasManagerRole(permissions) ||
    hasDoulaRole(permissions)
  );
};

/**
 * Whether the user sees the client Overview tab strip (Overview, Checklists and Flag Archive).
 * Every staff role qualifies except Biller, who sees the Overview content without tabs.
 */
const hasClientOverviewTabsPermission = (permissions: PractitionerPermissionFlags[]) => {
  return (
    hasManagerRole(permissions) ||
    hasProviderRole(permissions) ||
    hasScribeRole(permissions) ||
    hasReaderRole(permissions) ||
    hasDoulaRole(permissions)
  );
};

const hasBillerManagerPermission = (permissions: PractitionerPermissionFlags[]) => {
  return permissions.some((p) => BILLER_MANAGER_PERMISSION_FLAGS.includes(p));
};

const hasRequiredPermissions = (
  permissions: PractitionerPermissionFlags[],
  requiredPermissions: PractitionerPermissionFlags[],
) => {
  return requiredPermissions.some(
    (permission) =>
      permissions.includes(permission) || permissions.includes(PractitionerPermissionFlags.Admin),
  );
};

export {
  hasBillerManagerPermission,
  hasAdminProviderRole,
  hasAdminRole,
  hasAnyPermission,
  hasBillerRole,
  hasChartExportPermission,
  hasClientOverviewTabsPermission,
  hasDoulaRole,
  hasEncounterSignPermission,
  hasFlagWritePermission,
  hasFormsSendPermission,
  hasLabOrderPermission,
  hasLabResultsViewPermission,
  hasManagerRole,
  hasProviderOrScribeRole,
  hasProviderRole,
  hasReaderRole,
  hasRequiredPermissions,
  hasScribeRole,
  hasWriteRole,
};
